import { type DealDto, type ProspectDto } from "@crowdcoursing/api/openapi";
import { Box, Button, Link, Paper, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { Link as RouterLink } from "react-router-dom";

import { Route } from "~services/router/Route";
import { AddProspectToDealModal } from "~ui/molecules/AddProspectToDealModal/AddProspectToDealModal";

const tDeals = "Deals";
const tAddToDeal = "Add to Deal";
const tNoDeals = "This prospect is not linked to any deals yet.";
const tUntitledDeal = "Untitled deal";

export interface DealsTabProps {
  prospect: ProspectDto;
  deals: DealDto[];
  isLoadingDeals?: boolean;
  isAddingProspectToDeal?: boolean;
  onAddToDeal: (dealId: string) => Promise<void>;
  compact?: boolean;
}

export const DealsTab = ({
  prospect,
  deals,
  isLoadingDeals = false,
  isAddingProspectToDeal = false,
  onAddToDeal,
  compact = false,
}: DealsTabProps) => {
  const [isAddOpen, setIsAddOpen] = useState(false);

  const linkedDeals = useMemo(() => (prospect.deals ?? []).filter((deal) => Boolean(deal.id)), [prospect.deals]);

  const addToDealOptions = useMemo(() => {
    const linkedDealIds = new Set(linkedDeals.map((deal) => deal.id));
    return deals
      .filter((deal) => deal.id && !linkedDealIds.has(deal.id))
      .map((deal) => ({
        id: deal.id ?? "",
        name: deal.name?.trim() || tUntitledDeal,
      }));
  }, [deals, linkedDeals]);

  const heading = (
    <Box alignItems="center" display="flex" justifyContent="space-between" mb={compact ? 1 : 2}>
      <Typography id="deals-heading" variant={compact ? "subtitle2" : "h6"}>
        {tDeals}
      </Typography>
      <Button onClick={() => setIsAddOpen(true)} size="small" variant="outlined">
        {tAddToDeal}
      </Button>
    </Box>
  );

  const dealList =
    linkedDeals.length > 0 ? (
      <Stack spacing={1}>
        {linkedDeals.map((deal) => {
          const name = deal.name?.trim() || tUntitledDeal;
          return (
            <Link
              component={RouterLink}
              fontWeight={600}
              key={deal.id}
              to={Route.salesPortalDealDetails({ dealId: deal.id ?? "" })}
              underline="hover"
              variant="body1"
            >
              {name}
            </Link>
          );
        })}
      </Stack>
    ) : (
      <Typography color="text.secondary">{tNoDeals}</Typography>
    );

  return (
    <Box display="grid" gap={compact ? 0 : 3}>
      {compact ? (
        <Box component="section" role="region" aria-labelledby="deals-heading">
          {heading}
          {dealList}
        </Box>
      ) : (
        <Paper component="section" role="region" aria-labelledby="deals-heading" sx={{ p: 3 }}>
          {heading}
          {dealList}
        </Paper>
      )}
      <AddProspectToDealModal
        deals={addToDealOptions}
        isLoading={isLoadingDeals}
        isPending={isAddingProspectToDeal}
        onClose={() => setIsAddOpen(false)}
        onSubmit={onAddToDeal}
        open={isAddOpen}
        prospectName={prospect.name ?? undefined}
      />
    </Box>
  );
};
